import type { DealRepoData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import { formatSettlementCycle } from '@src/utils/OTC/otcUtils';
import {
  AssetRenderer,
  isOTCRepoSecurity,
  LoanQuoteTerminationTypeEnum,
  logger,
  prettyName,
  useSecuritiesContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { InspectorNumberDisplay } from './InspectorNumberDisplay';
import { InspectorPercentDisplay } from './InspectorPercentDisplay';
import { InspectorText } from './InspectorText';
import { InspectorSectionsRenderer } from './shared/InspectorSectionsRenderer';
import type { DisplayOutputItem } from './shared/types';
import { useCounterpartyInformation } from './shared/useCounterpartyInformation';
import { useTimingInformation } from './shared/useTimingInformation';

const useRepoDealInformation = (instrumentData: DealRepoData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isOTCRepoSecurity(dealSecurity) ? dealSecurity : undefined;
  if (!resolvedDealSecurity) {
    logger.error(new Error(`Failed to get repo deal security for symbol ${instrumentData.Symbol ?? '(missing)'}`), {
      extra: {
        dealSecurity,
        resolvedDealSecurity,
      },
    });
    return {
      sectionTitle: 'Deal Information',
      fields: [],
    };
  }

  const productDetails = resolvedDealSecurity.ProductDetails;
  const termType = productDetails?.TermType;
  const termTypeLabel = termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Open' : 'Fixed';

  return {
    sectionTitle: 'Deal Information',
    fields: compact([
      {
        fieldName: 'Term Type',
        fieldValue: <InspectorText>{termTypeLabel}</InspectorText>,
      },
      {
        fieldName: termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Initial Term' : 'Term',
        fieldValue: <InspectorText>{productDetails?.Term}</InspectorText>,
      },
      {
        fieldName: 'Direction',
        fieldValue: <InspectorText>{prettyName(instrumentData.DealInfo.Direction)}</InspectorText>,
      },
      {
        fieldName: 'Borrowed Asset',
        fieldValue: <AssetRenderer asset={productDetails?.BorrowedAsset} colorful />,
      },
      {
        fieldName: 'Repo Asset',
        fieldValue: <AssetRenderer asset={productDetails?.RepoAsset} colorful />,
      },
      {
        fieldName: 'Quantity',
        fieldValue: <InspectorText>{instrumentData.DealInfo.EnteredQuantity}</InspectorText>,
      },
      {
        fieldName: 'Repo Amount',
        fieldValue: (
          <InspectorNumberDisplay value={instrumentData.DealInfo.RepoAmount} currency={productDetails?.RepoAsset} />
        ),
      },
      {
        fieldName: 'Rate',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.Rate} />,
      },
      {
        fieldName: 'Prime Broker',
        fieldValue: <InspectorText>{instrumentData.DealInfo.PrimeBroker}</InspectorText>,
      },
      {
        fieldName: 'Settlement Cycle',
        fieldValue: <InspectorText>{formatSettlementCycle(instrumentData.DealInfo.SettleCycle)}</InspectorText>,
      },
    ]),
  };
};

const useRepoPricingInformation = (instrumentData: DealRepoData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isOTCRepoSecurity(dealSecurity) ? dealSecurity : undefined;
  const borrowedAsset = resolvedDealSecurity?.ProductDetails.BorrowedAsset;

  return {
    sectionTitle: 'Pricing Information',
    fields: [
      {
        fieldName: 'Reference Price',
        fieldValue: <InspectorNumberDisplay value={instrumentData.DealInfo.ReferencePrice} currency={borrowedAsset} />,
      },
      {
        fieldName: 'Reference Time',
        fieldValue: (
          <InspectorText>{formatOtcDealDateTimeInTimezone(instrumentData.DealInfo.ReferenceTime)}</InspectorText>
        ),
      },
      {
        fieldName: 'Purchase Price',
        fieldValue: <InspectorNumberDisplay value={instrumentData.DealInfo.PurchasePrice} currency={borrowedAsset} />,
      },
      {
        fieldName: 'Repo Interest',
        fieldValue: <InspectorNumberDisplay value={instrumentData.DealInfo.RepoInterest} currency={borrowedAsset} />,
      },
      {
        fieldName: 'Repurchase Price',
        fieldValue: <InspectorNumberDisplay value={instrumentData.DealInfo.RepurchasePrice} currency={borrowedAsset} />,
      },
    ],
  };
};

const useRepoMarginInformation = (instrumentData: DealRepoData): DisplayOutputItem => {
  return {
    sectionTitle: 'Margin Information',
    fields: [
      {
        fieldName: 'Repo Asset Haircut',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.HaircutPct} />,
      },
      {
        fieldName: 'Initial Margin %',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.InitialMarginPct} />,
      },
      {
        fieldName: 'Margin Release %',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.MarginReleasePct} />,
      },
      {
        fieldName: 'Maintenance Margin %',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.MaintenanceMarginPct} />,
      },
      {
        fieldName: 'Margin Call %',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.MarginCallPct} />,
      },
      {
        fieldName: 'Close-out %',
        fieldValue: <InspectorPercentDisplay value={instrumentData.DealInfo.CloseOutMarginPct} />,
      },
    ],
  };
};

interface RepoInstrumentInspectorSectionsProps {
  instrumentData: DealRepoData;
}

export const RepoInstrumentInspectorSections = ({ instrumentData }: RepoInstrumentInspectorSectionsProps) => {
  const tradeInfo = useRepoDealInformation(instrumentData);
  const pricingInfo = useRepoPricingInformation(instrumentData);
  const marginInfo = useRepoMarginInformation(instrumentData);
  const counterpartyInfo = useCounterpartyInformation(instrumentData);
  const timingInfo = useTimingInformation(instrumentData);

  const sections = [tradeInfo, pricingInfo, marginInfo, counterpartyInfo, timingInfo];

  return <InspectorSectionsRenderer sections={sections} />;
};
